import * as signalR from '@microsoft/signalr';
import { SignalRConstants } from './SignalRConstants';
import Config from 'app/Config';

// Create SignalR service as a function-based implementation
const createSignalRService = () => {
  // Private state variables (using closures)
  let connection = null;
  let idleTimer = null;
  let reconnectAttempts = 0;
  let isConnecting = false;
  let subscribers = new Map(); // Track event subscribers
  let heartbeatTimer = null;

  // Configuration constants
  const IDLE_TIMEOUT_MINUTES = 5;
  const IDLE_TIMEOUT_MS = IDLE_TIMEOUT_MINUTES * 60 * 1000;
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY_MS = 2000;
  const HEARTBEAT_INTERVAL = 30000; // 30 seconds

  // Helper function to get connection state
  const getConnectionState = () => {
    if (!connection) return SignalRConstants.ConnectionStates.DISCONNECTED;

    switch (connection.state) {
      case signalR.HubConnectionState.Connected:
        return SignalRConstants.ConnectionStates.CONNECTED;
      case signalR.HubConnectionState.Connecting:
        return SignalRConstants.ConnectionStates.CONNECTING;
      case signalR.HubConnectionState.Disconnected:
        return SignalRConstants.ConnectionStates.DISCONNECTED;
      case signalR.HubConnectionState.Disconnecting:
        return SignalRConstants.ConnectionStates.DISCONNECTING;
      case signalR.HubConnectionState.Reconnecting:
        return SignalRConstants.ConnectionStates.RECONNECTING;
      default:
        return 'Unknown';
    }
  };

  // Enhanced logging with proper error detection
  const log = (level, message, data = null, error = null) => {
    const timestamp = new Date().toISOString();
    const logPrefix = `[${timestamp}] SignalR`;

    const logData = {
      message,
      connectionState: getConnectionState(),
      reconnectAttempts,
      ...(data && { data }),
      ...(error && { error: error.message || error })
    };

    switch (level) {
      case SignalRConstants.LogLevels.TRACE:
        console.trace(`${logPrefix} [TRACE]:`, logData);
        break;
      case SignalRConstants.LogLevels.DEBUG:
        console.debug(`${logPrefix} [DEBUG]:`, logData);
        break;
      case SignalRConstants.LogLevels.INFO:
        console.info(`${logPrefix} [INFO]:`, logData);
        break;
      case SignalRConstants.LogLevels.WARN:
        console.warn(`${logPrefix} [WARN]:`, logData);
        break;
      case SignalRConstants.LogLevels.ERROR:
        console.error(`${logPrefix} [ERROR]:`, logData);
        break;
      default:
        console.log(`${logPrefix} [LOG]:`, logData);
    }
  };

  // Custom HTTP client with proper error handling and authentication
  const createHttpClient = () => {
    return new signalR.DefaultHttpClient({
      log: (logLevel, message) => {
        log(mapSignalRLogLevel(logLevel), `HTTP: ${message}`);
      }
    });
  };

  const mapSignalRLogLevel = (signalRLevel) => {
    switch (signalRLevel) {
      case signalR.LogLevel.Trace: return SignalRConstants.LogLevels.TRACE;
      case signalR.LogLevel.Debug: return SignalRConstants.LogLevels.DEBUG;
      case signalR.LogLevel.Information: return SignalRConstants.LogLevels.INFO;
      case signalR.LogLevel.Warning: return SignalRConstants.LogLevels.WARN;
      case signalR.LogLevel.Error: return SignalRConstants.LogLevels.ERROR;
      default: return SignalRConstants.LogLevels.DEBUG;
    }
  };

  const validateAuthToken = () => {
    const authToken = localStorage.getItem('auth_token');
    if (!authToken) {
      throw new Error('Authentication token not found');
    }

    const cleanToken = authToken.replace(/^"|"$/g, '');
    if (!cleanToken) {
      throw new Error('Authentication token is empty');
    }

    return cleanToken;
  };

  const resetIdleTimer = () => {
    if (idleTimer) {
      clearTimeout(idleTimer);
    }

    idleTimer = setTimeout(() => {
      log(SignalRConstants.LogLevels.INFO, `Connection idle for ${IDLE_TIMEOUT_MINUTES} minutes, disconnecting...`);
      stopConnection();
    }, IDLE_TIMEOUT_MS);
  };

  const startHeartbeat = () => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
    }

    heartbeatTimer = setInterval(async () => {
      if (isConnected()) {
        try {
          await connection.invoke('SendHeartbeat');
          log(SignalRConstants.LogLevels.DEBUG, 'Heartbeat sent');
        } catch (error) {
          log(SignalRConstants.LogLevels.WARN, 'Heartbeat failed', null, error);
        }
      }
    }, HEARTBEAT_INTERVAL);
  };

  const stopHeartbeat = () => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  const setupEventHandlers = () => {
    if (!connection) return;

    connection.onreconnecting((error) => {
      reconnectAttempts++;
      log(SignalRConstants.LogLevels.WARN, `Reconnecting...`, { attempt: reconnectAttempts }, error);
    });

    connection.onreconnected((connectionId) => {
      log(SignalRConstants.LogLevels.INFO, 'Reconnected successfully', { connectionId });
      reconnectAttempts = 0;
      resetIdleTimer();
      startHeartbeat();
    });

    connection.onclose((error) => {
      stopHeartbeat();

      if (idleTimer) {
        clearTimeout(idleTimer);
        idleTimer = null;
      }

      if (error) {
        log(SignalRConstants.LogLevels.ERROR, 'Connection closed with error', null, error);
      } else {
        log(SignalRConstants.LogLevels.INFO, 'Connection closed gracefully');
      }

      connection = null;
      reconnectAttempts = 0;
    });

    // Handle all core connection events from the hub
    connection.on(SignalRConstants.Events.Connected, (data) => {
      log(SignalRConstants.LogLevels.INFO, 'Connected event received from server', data);
      resetIdleTimer();
    });

    // Handle connection closed event from server
    connection.on(SignalRConstants.Events.ConnectionClosed, (data) => {
      log(SignalRConstants.LogLevels.INFO, 'Connection closed by server', {
        reason: data?.reason,
        timestamp: data?.timestamp
      });
      // The connection will be cleaned up automatically by the onclose handler
    });

    // Handle heartbeat response
    connection.on(SignalRConstants.Events.Heartbeat, (data) => {
      log(SignalRConstants.LogLevels.DEBUG, 'Heartbeat received', {
        timestamp: data?.timestamp || data
      });
      resetIdleTimer();
    });

    // Handle group join/leave confirmations
    connection.on(SignalRConstants.Events.JoinedGroup, (data) => {
      log(SignalRConstants.LogLevels.INFO, 'Successfully joined group', {
        groupName: data?.groupName || data,
        message: data?.message,
        timestamp: data?.timestamp
      });
    });

    connection.on(SignalRConstants.Events.LeftGroup, (data) => {
      log(SignalRConstants.LogLevels.INFO, 'Successfully left group', {
        groupName: data?.groupName || data,
        message: data?.message,
        timestamp: data?.timestamp
      });
    });

    // Handle error events from server
    connection.on(SignalRConstants.Events.Error, (data) => {
      log(SignalRConstants.LogLevels.ERROR, 'Error received from server', {
        message: data?.message || data,
        error: data?.error,
        timestamp: data?.timestamp
      });
    });
  };

  const initConnection = async () => {
    if (isConnecting) {
      log(SignalRConstants.LogLevels.WARN, 'Connection already in progress');
      return;
    }

    if (connection && connection.state === signalR.HubConnectionState.Connected) {
      log(SignalRConstants.LogLevels.WARN, 'Already connected');
      return;
    }

    try {
      isConnecting = true;
      const cleanToken = validateAuthToken();

      // Stop existing connection if any
      if (connection) {
        await stopConnection();
      }

      log(SignalRConstants.LogLevels.INFO, 'Initializing SignalR connection');

      connection = new signalR.HubConnectionBuilder()
        .withUrl(`${Config.apiGwUrl}${SignalRConstants.HUB_URL}`, {
          skipNegotiation: false,
          withCredentials: true,
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
          accessTokenFactory: () => {
            try {
              return validateAuthToken();
            } catch (error) {
              log(SignalRConstants.LogLevels.ERROR, 'Failed to get access token', null, error);
              throw error;
            }
          },
          timeout: 90000,
          serverTimeoutInMilliseconds: 100000,
          keepAliveIntervalInMilliseconds: 15000,
          // Additional headers for debugging
          headers: {
            'User-Agent': navigator.userAgent || 'SignalR-Client'
          }
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: retryContext => {
            if (retryContext.previousRetryCount >= MAX_RECONNECT_ATTEMPTS) {
              log(SignalRConstants.LogLevels.ERROR, 'Max reconnection attempts reached');
              return null;
            }
            const delay = Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
            log(SignalRConstants.LogLevels.INFO, `Reconnecting in ${delay}ms (attempt ${retryContext.previousRetryCount + 1})`);
            return delay;
          }
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      setupEventHandlers();

      // Add connection debugging
      log(SignalRConstants.LogLevels.INFO, 'Starting SignalR connection', {
        url: `${Config.apiGwUrl}${SignalRConstants.HUB_URL}`,
        transport: 'WebSockets with LongPolling fallback'
      });

      await connection.start();

      log(SignalRConstants.LogLevels.INFO, 'SignalR connection established successfully', {
        connectionId: connection.connectionId,
        transport: connection.transport?.name || 'unknown'
      });

      reconnectAttempts = 0;
      resetIdleTimer();
      startHeartbeat();

      return true;

    } catch (error) {
      log(SignalRConstants.LogLevels.ERROR, 'Failed to initialize connection', {
        url: `${Config.apiGwUrl}${SignalRConstants.HUB_URL}`,
        errorDetails: error.toString()
      }, error);
      connection = null;
      throw error;
    } finally {
      isConnecting = false;
    }
  };

  const setupConnection = async (subscriberNumber, groupType, eventCallbacks = {}) => {
    try {
      if (!subscriberNumber || !groupType) {
        throw new Error('subscriberNumber and groupType are required');
      }

      log(SignalRConstants.LogLevels.INFO, 'Setting up connection', { subscriberNumber, groupType });

      await initConnection();

      if (!isConnected()) {
        throw new Error('Failed to establish connection');
      }

      // Join the specified group
      const groupName = `${groupType}_${subscriberNumber}`;
      await joinGroup(groupName);

      // Setup event handlers
      setupEventCallbacks(eventCallbacks);

      log(SignalRConstants.LogLevels.INFO, 'Connection setup complete', { groupName });
      return true;

    } catch (error) {
      log(SignalRConstants.LogLevels.ERROR, 'Connection setup failed', { subscriberNumber, groupType }, error);
      throw error;
    }
  };

  const setupEventCallbacks = (eventCallbacks) => {
    if (!connection || !eventCallbacks) return;

    Object.entries(eventCallbacks).forEach(([eventName, callback]) => {
      // Remove existing handler if any
      connection.off(eventName);

      // Add new handler
      connection.on(eventName, (data) => {
        log(SignalRConstants.LogLevels.DEBUG, `Received event: ${eventName}`, { data });
        resetIdleTimer();

        if (callback && typeof callback === 'function') {
          try {
            callback(data);
          } catch (callbackError) {
            log(SignalRConstants.LogLevels.ERROR, `Error in ${eventName} callback`, null, callbackError);
          }
        }
      });

      log(SignalRConstants.LogLevels.DEBUG, `Event handler registered: ${eventName}`);
    });
  };

  const joinGroup = async (groupName) => {
    if (!isConnected()) {
      throw new Error('Not connected to SignalR hub');
    }

    try {
      await connection.invoke('JoinGroup', groupName);
      log(SignalRConstants.LogLevels.INFO, 'Joined group', { groupName });
      return true;
    } catch (error) {
      log(SignalRConstants.LogLevels.ERROR, 'Failed to join group', { groupName }, error);
      throw error;
    }
  };

  const leaveGroup = async (groupName) => {
    if (!isConnected()) {
      log(SignalRConstants.LogLevels.WARN, 'Cannot leave group - not connected', { groupName });
      return false;
    }

    try {
      await connection.invoke('LeaveGroup', groupName);
      log(SignalRConstants.LogLevels.INFO, 'Left group', { groupName });
      return true;
    } catch (error) {
      log(SignalRConstants.LogLevels.ERROR, 'Failed to leave group', { groupName }, error);
      throw error;
    }
  };

  const sendMessage = async (methodName, ...args) => {
    if (!isConnected()) {
      throw new Error('SignalR connection is not active');
    }

    try {
      const result = await connection.invoke(methodName, ...args);
      log(SignalRConstants.LogLevels.DEBUG, `Method invoked: ${methodName}`, { args, result });
      return result;
    } catch (error) {
      log(SignalRConstants.LogLevels.ERROR, `Failed to invoke method: ${methodName}`, { args }, error);
      throw error;
    }
  };

  const stopConnection = async () => {
    if (!connection) {
      log(SignalRConstants.LogLevels.DEBUG, 'No connection to stop');
      return;
    }

    try {
      stopHeartbeat();

      if (idleTimer) {
        clearTimeout(idleTimer);
        idleTimer = null;
      }

      const currentState = connection.state;
      log(SignalRConstants.LogLevels.INFO, 'Stopping connection', { currentState: getConnectionState() });

      if (currentState === signalR.HubConnectionState.Connected ||
          currentState === signalR.HubConnectionState.Connecting ||
          currentState === signalR.HubConnectionState.Reconnecting) {
        await connection.stop();
      }

      connection = null;
      reconnectAttempts = 0;
      log(SignalRConstants.LogLevels.INFO, 'Connection stopped successfully');

    } catch (error) {
      log(SignalRConstants.LogLevels.ERROR, 'Error stopping connection', null, error);
      connection = null;
      reconnectAttempts = 0;
    }
  };

  const isConnected = () => {
    return connection?.state === signalR.HubConnectionState.Connected;
  };

  const resetConnectionIdleTimer = () => {
    if (isConnected()) {
      resetIdleTimer();
    }
  };

  // Utility methods for troubleshooting scenarios only
  const joinTroubleshootingGroup = async (subscriberNumber) => {
    const groupName = SignalRConstants.Groups.Troubleshooting(subscriberNumber);
    return await joinGroup(groupName);
  };

  const leaveTroubleshootingGroup = async (subscriberNumber) => {
    const groupName = SignalRConstants.Groups.Troubleshooting(subscriberNumber);
    return await leaveGroup(groupName);
  };

  // Return public API - only troubleshooting functionality
  return {
    setupConnection,
    isConnected,
    getConnectionState,
    stopConnection,
    resetConnectionIdleTimer,
    sendMessage,
    joinGroup,
    leaveGroup,
    joinTroubleshootingGroup,
    leaveTroubleshootingGroup,
  };
};

// Create singleton instance
const signalRService = createSignalRService();

// Export the service instance directly
export default signalRService;
